/* eslint-disable */
// React hook for Event API subscriptions
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import eventService from '../services/eventService';

// Hook for real-time notifications
export const useNotifications = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const subscriptionRef = useRef(null);

  const handleNewNotification = useCallback((notification) => {
    console.log('🔔 New notification received:', notification);
    setNotifications(prev => [notification, ...prev]);
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (user?.id && !subscriptionRef.current) {
      console.log('🔔 Setting up notification subscription for user:', user.id);
      
      try {
        subscriptionRef.current = eventService.subscribeToNotifications(
          user.id,
          handleNewNotification
        );
        console.log('✅ Notification subscription created successfully');
      } catch (error) {
        console.error('❌ Failed to create notification subscription:', error);
      }

      return () => {
        if (subscriptionRef.current) {
          console.log('🧹 Cleaning up notification subscription');
          eventService.unsubscribe(`notifications_${user.id}`);
          subscriptionRef.current = null;
        }
      };
    }
  }, [user?.id]);

  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, isRead: true }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const addTestNotification = useCallback((notification) => {
    console.log('🧪 Adding test notification:', notification);
    setNotifications(prev => [notification, ...prev]);
    if (!notification.isRead) {
      setUnreadCount(prev => prev + 1);
    }
  }, []);

  return {
    notifications,
    unreadCount,
    markAsRead,
    clearAll,
    addTestNotification
  };
};

// Hook for real-time chat
export const useChat = (conversationID) => {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const subscriptionRef = useRef(null);

  const handleNewMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
  }, []);

  useEffect(() => {
    if (conversationID && !subscriptionRef.current) {
      console.log('💬 Setting up chat subscription for conversation:', conversationID);
      setIsConnected(true);
      
      subscriptionRef.current = eventService.subscribeToChat(
        conversationID,
        handleNewMessage
      );

      return () => {
        if (subscriptionRef.current) {
          eventService.unsubscribe(`chat_${conversationID}`);
          subscriptionRef.current = null;
          setIsConnected(false);
        }
      };
    }
  }, [conversationID]);

  const { user } = useAuth();
  
  const sendMessage = useCallback(async (content, receiverID, messageType = 'text') => {
    if (!user?.id || !conversationID) return null;

    try {
      return await eventService.sendMessage(
        conversationID,
        user.id,
        receiverID,
        content,
        messageType
      );
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, [conversationID]);

  return {
    messages,
    isConnected,
    sendMessage
  };
};

// Hook for Event API status monitoring
export const useEventAPIStatus = () => {
  const [status, setStatus] = useState({
    activeSubscriptions: 0,
    isHealthy: true,
    lastUpdate: null
  });

  useEffect(() => {
    const updateStatus = () => {
      setStatus({
        activeSubscriptions: eventService.getActiveSubscriptionCount(),
        isHealthy: true,
        lastUpdate: new Date()
      });
    };

    // Update status every 30 seconds
    const interval = setInterval(updateStatus, 30000);
    updateStatus(); // Initial update

    return () => clearInterval(interval);
  }, []);

  return status;
};

// Hook for testing subscriptions (removed sendNotification)
export const useNotificationTester = () => {
  const { user } = useAuth();
  
  const testSubscription = useCallback((message) => {
    if (!user?.id) return;
    
    // Simulate a notification by directly calling the subscription callback
    const mockNotification = {
      id: `test-${Date.now()}`,
      userID: user.id,
      type: 'INFO',
      message: message || 'Test subscription notification',
      isRead: false,
      metadata: JSON.stringify({ source: 'test', timestamp: new Date().toISOString() }),
      createdAt: new Date().toISOString()
    };
    
    console.log('🧪 Testing subscription with mock notification:', mockNotification);
    
    // Find the subscription and trigger it
    const subscriptionKey = `notifications_${user.id}`;
    if (eventService.subscriptions && eventService.subscriptions.has(subscriptionKey)) {
      // This would normally come from the GraphQL subscription
      // For testing, we'll add the notification directly to the hook state
      return mockNotification;
    } else {
      console.warn('No active subscription found for testing');
      return null;
    }
  }, [user?.id]);

  return { testSubscription };
};